import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { ApplyDiscountVoucher } from '@moonpig/web-shared-promotions'
import {
  GetInitialPropsReturn,
  PageContext,
  PageEnvironment,
  PageRedirectResponse,
} from '@moonpig/web-core-app'
import { router, Routes, useRouter } from '@moonpig/web-core-routing'
import {
  RecentlyViewedProvider,
  FavouritesProvider,
  BasketProvider,
  DispatchCutOffQuery,
  DispatchCutOffQueryVariables,
  DispatchCutOffGQL,
  createViewItemListGAEvent,
  PageTypeEnum,
  DispatchBanner,
} from '@moonpig/web-shared-products'
import {
  createScreenViewEvent,
  trackGAEvent,
  useTrackGAEventOnce,
} from '@moonpig/web-core-analytics'
import {
  DepartmentsEnum,
  Region as GraphQLRegion,
  IntegerFilterInput,
  SortOrder,
} from '@moonpig/web-explore-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import {
  getParentDepartment,
  getSearchFilterWithLabel,
  getSearchCriteria,
  parseUrlQuerySearchTerm,
} from '@moonpig/web-core-utils'
import { Region } from '@moonpig/web-core-types'
import { useApolloClient } from '@moonpig/web-core-graphql'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { sanitize, sanitizeCollection, useScrollRestoration } from '../../utils'
import { notRudeFilter } from '../../constants'
import { SearchHead } from './SearchHead'
import {
  SearchFeatures,
  SearchFilter,
  ServiceOptions,
  createServices,
  loadFeatures,
  loadOptions,
} from '../../services'
import {
  LoadFiltersResponse,
  SearchRequestResponse,
} from '../../services/types/services'
import { getGAListDataType } from '../../utils/getGAListDataType'
import { SearchDetails, SelectedSuggestion } from '../../components/types'
import { DrawerProvider } from '../../contexts/drawer'
import { PageType, RedirectionType } from '../../types'
import { SearchWrapper } from './SearchWrapper'
import { SearchStoreProvider } from '../../store/SearchStore'
import { SearchStoreState } from '../../store/types'
import {
  applySelectedFilters,
  buildInitialState,
  getPersistedFilters,
  getSelectedFilters,
  removePreAppliedFilters,
  extractIntegerFilter,
} from '../../store/helpers'
import { useNbaFilters } from '../../utils/nbaFilters'
import { getIsFilterApplied } from '../../utils/getIsFilterApplied'
import { createViewResultsEvent } from '../../analytics/createViewResultsEvent'
import { resolveDepartmentForResults } from './resolveDepartmentForResults'
import { useAppendSearchHistory } from './useAppendSearchHistory'

// eslint-disable-next-line @typescript-eslint/ban-types
export type PageContent<TProps = {}> = FC<TProps> & {
  getInitialProps(
    context: PageContext<Routes['search']> & {
      pageType?: PageTypeEnum
      dynamicSearchTerm?: string
      dynamicDepartment?: DepartmentsEnum[]
      isIndexed?: boolean
    },
  ): Promise<PageRedirectResponse | GetInitialPropsReturn<TProps>>
}

type SearchPageProps = {
  initialFiltersResponse: LoadFiltersResponse
  initialResponse: SearchRequestResponse
  page: number
  region: Region
  details: SearchDetails
  filters: SearchFilter[]
  experiments?: { [key: string]: string } | undefined
  query: Routes['search'] | Routes['pay-per-click']
  dispatchCutOffData?: DispatchCutOffQuery
  environment?: PageEnvironment
  isIndexed: boolean
  selectedSuggestion?: SelectedSuggestion
  groupCardProject?: string
  options: ServiceOptions
  features: SearchFeatures
  luxLabel: string
  pageType: PageTypeEnum
  pageTitle: string
  headOverride?: ReactElement
  redirectedFrom?: DepartmentsEnum
  useClientSideRouting?: boolean
}

export const SearchPage: PageContent<SearchPageProps> = ({
  page: initialPage,
  initialFiltersResponse,
  initialResponse,
  region,
  details,
  filters: initialFilters,
  experiments,
  query,
  dispatchCutOffData,
  selectedSuggestion,
  options,
  features,
  headOverride,
  luxLabel,
  pageType,
  pageTitle,
  redirectedFrom,
  useClientSideRouting = true,
}) => {
  const { department, searchTerm, isTrendingSuggestion, redirectionType } =
    details
  const { galleryPageSize } = features
  const client = useApolloClient()
  const head = headOverride || <SearchHead region={region} page={initialPage} />

  const useNamedEntityRecognition =
    useExperiment('search-use-named-entity-recognition', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  useExperiment('rlp-to-search-redirect', {
    fallback: 'control',
  })

  const services = useMemo(() => {
    const context = {
      region,
      options,
      features,
      client,
      cookies: getBrowserCookies(),
    }

    return createServices(context)
  }, [client, features, options, region])

  const routerHook = useRouter()

  useScrollRestoration()

  useAppendSearchHistory({
    pageType,
    department,
    searchTerm,
    suggestionKey: query.suggestion,
    selectedSuggestion,
    totalCount: initialResponse.totalCount,
  })

  const { trackGAEventOnce } = useTrackGAEventOnce()

  const filtersQuery = ({
    filters,
    integerFilter,
  }: {
    filters: SearchFilter[]
    integerFilter?: IntegerFilterInput
  }) =>
    services.loadFilters({
      facets: filters,
      integerFilter: integerFilter || {},
      departments: department,
      promotionId: details.promotionId,
      searchTerm: details.correctedSearchTerm || searchTerm,
    })

  const preAppliedFilters =
    selectedSuggestion?.facetKey && selectedSuggestion?.group
      ? [
          {
            group: selectedSuggestion.group,
            key: selectedSuggestion.facetKey,
            suggestion: true,
          },
        ]
      : []

  const persistedFilters = getPersistedFilters()

  const hasNbaFilters = useNbaFilters(initialFiltersResponse.filters).length > 0
  const hasRudeFilterEnabled = getIsFilterApplied(
    initialFiltersResponse.filters,
    'not-rude',
    false,
  )

  const createInitialState = () =>
    buildInitialState({
      pageTitle,
      pageType: pageType as PageType,
      router: routerHook,
      department,
      initialResponse,
      selectedSuggestion,
      isTrendingSuggestion: details.isTrendingSuggestion,
      redirectedFrom,
      redirectionSource: redirectionType,
      searchTerm,
      correctedSearchTerm: details.correctedSearchTerm,
      promotionId: details.promotionId,
      collection: query.collection,
      initialFilters,
      persistedFilters,
      preAppliedFilters,
      allFilters: applySelectedFilters(
        removePreAppliedFilters(
          initialFiltersResponse.filters,
          preAppliedFilters,
        ),
        [...persistedFilters, ...initialFilters.map(f => f.key)],
      ),
      quickFilterKeys: initialFiltersResponse.quickFilterKeys ?? [],
      selectedFilters: getSelectedFilters({
        initialFilters,
        persistedFilters,
        allFilters: initialFiltersResponse.filters,
        useAutoAppliedFilters: useNamedEntityRecognition,
      }),
      hasNbaFilters,
      hasRudeFilterEnabled,
      filtersQuery,
      services,
      sponsoredProducts: [],
      offset: initialPage * galleryPageSize - galleryPageSize,
      galleryPageSize: galleryPageSize,
    })

  const [initialState, setInitialState] =
    useState<Partial<SearchStoreState>>(createInitialState())

  useEffect(() => {
    if (
      initialState.searchContext?.searchTerm !== searchTerm ||
      initialState.searchContext?.departments[0] !== department[0] ||
      initialState.searchContext?.selectedSuggestion?.facetKey !==
        selectedSuggestion?.facetKey
    ) {
      setInitialState({
        ...initialState,
        ...createInitialState(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedSuggestion, details.promotionId, department[0]])

  useEffect(() => {
    const initialIntegerFilter = extractIntegerFilter(
      initialFiltersResponse.filters,
    )
    const screenClass =
      pageType === PageTypeEnum.COLLECTION
        ? 'collection results'
        : 'search results'

    const screenName =
      pageType === PageTypeEnum.COLLECTION
        ? `${screenClass} | ${pageTitle}`.toLowerCase()
        : `${screenClass} | ${selectedSuggestion?.label ?? details.searchTerm}`.toLowerCase()

    trackGAEventOnce(
      createScreenViewEvent({
        screenClass,
        screenName,
        screenData: {
          renderType: 'client',
          documentReferrer: document.referrer,
          documentTitle: document.title.toLowerCase(),
          documentUrl: document.location.href,
        },
      }),
    )

    trackGAEventOnce(
      createViewResultsEvent({
        pageType,
        department,
        filters: [
          ...initialFilters,
          ...(initialIntegerFilter
            ? [
                {
                  key:
                    initialIntegerFilter.integerKey ||
                    /* istanbul ignore next */ '',
                  group: initialIntegerFilter.name,
                },
              ]
            : []),
        ],
        numberOfResults: initialResponse.totalCount,
        pageTitle,
        sort:
          initialState.sortValue ||
          /* istanbul ignore next */ SortOrder.POPULARITY,
        showNbas: hasNbaFilters,
        showRudeProducts: hasRudeFilterEnabled,
        redirectedFrom,
        redirectionSource: redirectionType,
        selectedSuggestion,
        isTrendingSuggestion,
        filteringEvent: initialState.filteringEvent,
        searchTerm: details.searchTerm,
        correctedSearchTerm: details.correctedSearchTerm || '',
      }),
    )

    /* istanbul ignore else */
    if (initialState.results) {
      const search_data = {
        index: '',
        input_type: 'keyword',
        number_of_results: initialState.results.totalCount,
        product_department: department[0],
        search_term: searchTerm,
        page_title: pageTitle,
      }
      const collection_data = {
        index: '',
        input_type: 'collection',
        number_of_results: initialState.results.totalCount,
        product_department: department[0],
        mission: query.mission,
        search_term: '',
        page_title: pageTitle,
      }
      const label = `${pageType} | ${department[0]} | ${searchTerm || 'predefined'} | ${pageTitle}`
      const event = createViewItemListGAEvent({
        products: initialState.results.products,
        label,
        search_data,
        collection_data:
          pageType === PageTypeEnum.COLLECTION ? collection_data : undefined,
      })
      trackGAEvent(event)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState])

  const dispatchCutOff =
    dispatchCutOffData?.contentDepartments[0]?.dispatchCutOff

  return (
    <RecentlyViewedProvider>
      <BasketProvider>
        <FavouritesProvider location="search" enableToastNotification>
          <DrawerProvider>
            {dispatchCutOff && (
              <DispatchBanner dispatchCutOff={dispatchCutOff} />
            )}
            {head}
            <ApplyDiscountVoucher uqd={query.uqd} />
            <SearchStoreProvider initialState={initialState}>
              <SearchWrapper
                luxLabel={luxLabel}
                useClientSideRouting={useClientSideRouting}
                experiments={experiments}
                pageTitle={pageTitle}
                groupCardProject={query.groupCardProject}
                pageType={pageType}
                department={department}
                searchTerm={searchTerm}
                selectedSuggestion={selectedSuggestion}
              />
            </SearchStoreProvider>
          </DrawerProvider>
        </FavouritesProvider>
      </BasketProvider>
    </RecentlyViewedProvider>
  )
}

SearchPage.getInitialProps = async ({
  region,
  query: typedQuery,
  path,
  graphQL,
  getAllExperimentVariants,
  getFlag,
  environment,
  getCookie,
  pageType = PageTypeEnum.SEARCH,
  platform,
  dynamicSearchTerm,
  dynamicDepartment,
  isIndexed,
  getExperiment,
}) => {
  let redirectionType = typedQuery.redirectionType as
    | RedirectionType
    | undefined
  let redirectedFrom = typedQuery.redirectedDepartment as
    | DepartmentsEnum
    | undefined

  const experiments = await getAllExperimentVariants()
  const useServerSideSDR = getExperiment('search-server-side-sdr', {
    fallback: false,
  })

  const {
    department: queryDepartment,
    filters: urlFacets,
    integerFilters,
    promotionId,
  } = getSearchCriteria(typedQuery)

  const department = dynamicDepartment || queryDepartment

  const isAllDepartmentUrl =
    Boolean(useServerSideSDR) && typedQuery.d?.toLowerCase() === 'all'
  const searchDepartments = isAllDepartmentUrl
    ? [DepartmentsEnum.ALL_CARDS]
    : department
  const parentDepartment = getParentDepartment(
    isAllDepartmentUrl ? DepartmentsEnum.ALL_CARDS : department[0],
  )

  const options = loadOptions({
    query: typedQuery,
    pageType,
    platform,
    department: parentDepartment,
  })

  const features = loadFeatures({
    experiments,
    region,
    getFlag,
  })

  const cookies = {
    mnpg_exclude_rude_products: getCookie('mnpg_exclude_rude_products'),
  }

  const context = {
    region,
    options,
    features,
    client: graphQL,
    cookies,
  }

  const services = createServices(context)

  const query = typedQuery as unknown as PageContext['query']

  const selectedSuggestion = getSearchFilterWithLabel(
    query.suggestion as string,
  )

  const selectedTrendingSuggestion = parseUrlQuerySearchTerm(
    query.trending_suggestion as string,
  )

  const excludeRude =
    cookies.mnpg_exclude_rude_products === 'true' &&
    parentDepartment === ('ALL_CARDS' as DepartmentsEnum)

  const combinedFiltersWithoutSuggestion = [
    ...urlFacets,
    ...(excludeRude ? [notRudeFilter] : []),
  ].map(f => ({
    key: f.facetKey,
    group: f.group,
    userApplied: f.userApplied,
  }))

  const combinedFiltersWithSuggestion: SearchFilter[] = [
    ...combinedFiltersWithoutSuggestion,
    ...(selectedSuggestion?.facetKey && selectedSuggestion?.group
      ? [
          {
            key: selectedSuggestion.facetKey,
            group: selectedSuggestion.group,
            userApplied: true,
            suggestion: true,
          },
        ]
      : []),
  ]

  const searchTerm =
    dynamicSearchTerm ||
    parseUrlQuerySearchTerm(typedQuery.q) ||
    selectedTrendingSuggestion ||
    ''
  const isLegacySearchUrl =
    typedQuery.f || typedQuery.product_categories || typedQuery.fq

  if (isLegacySearchUrl) {
    logger.info(`Legacy search URL transform - query: ${JSON.stringify(query)}`)
  }

  const galleryPageSize = features.galleryPageSize

  const page = (Number(query.offset || 0) || 0) / galleryPageSize + 1

  const isNERExperiment =
    getExperiment('search-use-named-entity-recognition', {
      fallback: 'disabled',
    })?.toLowerCase() === 'enabled'

  const usesSmartDepartmentRedirection = typedQuery.smartRedirection

  try {
    const isInitialQuery =
      isNERExperiment || isAllDepartmentUrl || usesSmartDepartmentRedirection

    const searchQuery = services.search({
      searchTerm,
      filters: combinedFiltersWithSuggestion,
      integerFilter: integerFilters[0],
      departments: isAllDepartmentUrl ? searchDepartments : department,
      useServerSideSDR: isAllDepartmentUrl,
      page,
      sponsoredProducts: [],
      promotionId,
      isInitialQuery,
    })

    const initialResponse = await searchQuery

    const departmentForResults = resolveDepartmentForResults({
      redirectedDepartment: initialResponse.redirectedDepartment,
      isAllDepartmentUrl,
      urlDepartment: department,
    })
    const resultsParentDepartment = getParentDepartment(departmentForResults[0])

    if (initialResponse.redirectedDepartment && !redirectionType) {
      redirectionType = 'smart_redirection'
      redirectedFrom = parentDepartment
    }

    const { spellingCorrection, totalCount } = initialResponse

    const shouldClientRedirectForSmartSDR =
      !isAllDepartmentUrl &&
      usesSmartDepartmentRedirection &&
      initialResponse.redirectedDepartment

    if (shouldClientRedirectForSmartSDR) {
      const { as } = router.build('search', {
        region,
        ...query,
        d: initialResponse.redirectedDepartment,
        smartRedirection: undefined,
      })

      redirectionType = 'smart_redirection'

      return {
        pageRedirect: {
          statusCode: 301,
          location: `${as}&redirectedDepartment=${parentDepartment}&redirectionType=${redirectionType}`,
        },
      }
    }

    if (initialResponse.departments.length > 0) {
      const departmentToRedirect = initialResponse.departments.reduce(
        (maxObject, currentObject) => {
          // istanbul ignore next
          return currentObject.count > maxObject.count
            ? currentObject
            : maxObject
        },
        initialResponse.departments[0],
      )

      if (initialResponse.totalCount === 0 && departmentToRedirect?.count > 0) {
        const { as } = router.build('search', {
          region,
          ...query,
          d: departmentToRedirect.department,
        })

        redirectedFrom = parentDepartment
        redirectionType = 'no_results'

        return {
          pageRedirect: {
            statusCode: 301,
            location: `${as}&redirectedDepartment=${parentDepartment}&redirectionType=${redirectionType}`,
          },
        }
      }
    }

    const [{ data: dispatchCutOffData }, initialFiltersResponse] =
      await Promise.all([
        graphQL.query<DispatchCutOffQuery, DispatchCutOffQueryVariables>({
          query: DispatchCutOffGQL,
          variables: {
            filter: [departmentForResults[0], resultsParentDepartment],
            region: region as GraphQLRegion,
            preview: undefined,
          },
        }),
        services.loadFilters({
          facets: combinedFiltersWithSuggestion,
          integerFilter: integerFilters[0],
          departments: departmentForResults,
          promotionId,
          searchTerm: spellingCorrection?.correctedSearchTerm || searchTerm,
          isInitialQuery,
        }),
      ])

    return {
      initialFiltersResponse,
      initialResponse,
      page,
      region: region as Region,
      details: {
        region,
        department: departmentForResults,
        url: path,
        searchTerm,
        isTrendingSuggestion: !!selectedTrendingSuggestion,
        promotionId,
        correctedSearchTerm: spellingCorrection?.correctedSearchTerm,
        redirectionType,
      },
      experiments,
      filters: combinedFiltersWithoutSuggestion,
      query: typedQuery,
      pageOptions: {
        layout: {
          nonStickyHeader: true,
        },
        tracking: {
          type: getGAListDataType(pageType),
          disableDefaultPageTracking: true,
          sortby: 'popularity - default',
          listData: {
            results: totalCount,
            type: getGAListDataType(pageType),
            name: `${pageType} - ${sanitize(searchTerm)}`,
            terms: sanitize(searchTerm) || '',
            filters: {
              facets: sanitizeCollection(combinedFiltersWithSuggestion),
              promotionId: sanitize(promotionId),
            },
          },
        },
      },
      dispatchCutOffData,
      environment,
      selectedSuggestion,
      groupCardProject: typedQuery.groupCardProject,
      options,
      features,
      luxLabel: 'Search Results Page',
      pageType,
      pageTitle: 'Search Results',
      isIndexed: isIndexed || false,
      redirectedFrom,
    }
  } catch (error) {
    return {
      pageError: {
        statusCode: 500,
      },
    }
  }
}
